/* @flow */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { TimelineMax, TweenMax, Power4, Power3 } from 'gsap';
import Card from 'app/components/commons/Card';
import GroupDetails from 'app/components/friends/groups/GroupDetails';
import GroupContentsExisting from 'app/components/friends/GroupContentsExisting';
import CarouselScrollable from 'app/components/friends/common/CarouselScrollable';
import GroupCard from 'app/components/friends/groups/GroupCard';
import { NEW_GROUP_ID } from 'app/constants/friendConstants';
import t from 'app/utils/translate';

type Props = {
  classes: Object,
  onCardClick?: (id: number | string) => void,
  currentGroupId?: number | string,
  groups?: Array<FriendGroup>,
  history: Object,
  open?: boolean,
  onOpenChangeDone: (isOpen: boolean) => void,
};

type State = {
  animating: boolean,
  isOpen: boolean,
};

class GroupCardsMobile extends Component<Props, State> {
  props: Props;
  closedCarouselRef: HTMLElement; // eslint-disable-line react/sort-comp
  openCarouselRef: HTMLElement;

  constructor(props: Props) {
    super(props);
    this.state = {
      animating: false,
      isOpen: !!props.open,
    };
  }

  static defaultProps = {
    currentGroupId: undefined,
    groups: [],
    onCardClick: undefined,
    open: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.open !== this.props.open) {
      this.setState({ animating: true });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const isOpening = !prevProps.open && this.props.open;
    const isClosing = prevProps.open && !this.props.open;

    if (isOpening) {
      this.animateOpen().then(() => {
        this.setState({ animating: false, isOpen: true }, () => {
          this.props.onOpenChangeDone(true);
        });
      });
    }

    if (isClosing) {
      this.animateClose().then(() => {
        this.setState({ animating: false, isOpen: false }, () => {
          this.props.onOpenChangeDone(false);
        });
      });
    }
  }

  setOpenRef = (node: HTMLElement) => {
    this.openCarouselRef = node;
  }

  setClosedRef = (node: HTMLElement) => {
    this.closedCarouselRef = node;
  }

  animateOpen = () => new Promise((resolve) => {
    const { groups, currentGroupId } = this.props;

    const openCarousel = ReactDOM.findDOMNode(this.openCarouselRef);
    const closedCarousel = ReactDOM.findDOMNode(this.closedCarouselRef);

    /**
     * Timeline:
     * fade the carousel out - 0.2s
     * push title across - 0.2s
     * grow the cards - 0.5s
     * fade the content in - 0.2s
     * scroll the content - 0.8s
     * total time: 1.9s
     */

    TweenMax.set(openCarousel, { zIndex: 1 });
    TweenMax.set(closedCarousel, { position: 'absolute', left: 0, top: 0, right: 0, zIndex: 2 });
    const height = window.innerHeight - openCarousel.getBoundingClientRect().top;
    const width = window.innerWidth - 20;
    const children = [...openCarousel.children];
    const timeline = new TimelineMax();
      // fade out closed carousel out that was sitting on top of the open one
    timeline.add(TweenMax.fromTo(closedCarousel, 0.2, { opacity: 1 }, { opacity: 0 }), 0);
      // grow our children to the specified size after it's disappeared (takes 1 second)
    children.forEach((child) => {
      if (!child.className.startsWith('dodgyPaddingHack')) {
        timeline.add(this.getGroupGrow(child, height, width), 0.2);
          // The 'my friends' is still clearing out for 0.5 sec
          // so wait till it's gone, then clear all our props
        timeline.add(() => TweenMax.set(child, { clearProps: 'all' }), 1.7);
      }
    });
      // finally, finish up by clearing all props and updating state with it being done
    timeline.add(() => {
      const currentIndex = groups.findIndex(g => g.id === currentGroupId);
      const isLast = currentIndex === children.length - 2;
      const scrollChild = children[currentIndex];
      if (scrollChild) {
        TweenMax.to(openCarousel, 0.8, {
          scrollTo: { x: scrollChild, offsetX: isLast ? 0 : 10 },
          ease: Power3.easeInOut,
        });
      }
      resolve();
      TweenMax.set(openCarousel, { clearProps: 'all' });
    }, 0.9);

    timeline.play();
  })

  animateClose = () => {
    const openCarousel = ReactDOM.findDOMNode(this.openCarouselRef);
    const closedCarousel = ReactDOM.findDOMNode(this.closedCarouselRef);
    return new Promise((resolve) => {
      TweenMax.set(openCarousel, { zIndex: 2 });
      TweenMax.set(closedCarousel, { position: 'absolute', left: 0, top: 0, right: 0, zIndex: 1, display: 'none' });

      /**
       * Timeline:
       * fade overlay in - 0.2s
       * shrink the groups - 0.5s
       * move the title across - 0.2s
       * fade out the open carousel - 0.2s
       * scroll to first - 0.8s
       * total time: 1.9s
       */

      const height = window.innerHeight - openCarousel.getBoundingClientRect().top;
      const width = window.innerWidth - 20;

      const children = [...openCarousel.children];
      const timeline = new TimelineMax();
      timeline.pause();
      // shrink all the open groups...
      children.forEach((child) => {
        if (!child.className.startsWith('dodgyPaddingHack')) {
          timeline.add(this.getGroupShrink(child, height, width), 0);
        }
      });

      timeline.add(() => TweenMax.set(closedCarousel, { display: '' }), 0.7);
      // fade out the open carousel to reveal the closed one behind it
      timeline.add(() => TweenMax.fromTo(openCarousel, 0.2, { opacity: 1 }, { opacity: 0 }), 0.7);

      // finally, finish up by clearing all props and updating state with it being done
      timeline.add(() => {
        const firstChild = [...closedCarousel.children][0];
        if (firstChild) {
          TweenMax.to(closedCarousel, 1, {
            scrollTo: { x: firstChild, offsetX: 10 },
            ease: Power3.easeInOut,
          });
        }
        TweenMax.set(closedCarousel, { clearProps: 'all' });
      }, 0.9);

      // Our overlay is now fading in...
      // once it's in, update the state so we stop trying to render the group
      window.setTimeout(() => {
        this.setState({ isOpen: false }, () => resolve(timeline));
      }, 200);
    })
    .then(timeline => new Promise((resolve) => {
      timeline.add(() => resolve());
        // our group should be unrendered now, so play our animation
      timeline.play();
    }));
  }

  getGroupGrow = (node, height, width) => {
    const timeline = new TimelineMax();
    const pusherNode = node.querySelector('.pusher');

    timeline.add(TweenMax.fromTo(pusherNode, 0.2,
      { flexBasis: '100%' },
      { flexBasis: '0%', ease: Power3.easeInOut }),
    0);
    timeline.add(TweenMax.fromTo(node, 0.5,
      { width: 155, height: 90, flexGrow: 0, z: 0 },
      { width,
        height,
        ease: Power4.easeInOut }), 0.2);
    return timeline;
  }

  getGroupShrink = (node, height, width) => {
    const timeline = new TimelineMax();
    const pusherNode = node.querySelector('.pusher');

    timeline.add(TweenMax.fromTo(node, 0.5,
      { width, height, flexGrow: 0, borderRadius: 8, overflow: 'hidden', z: 0 },
      { width: 155, height: 90, ease: Power4.easeInOut }), 0);
    timeline.add(() => TweenMax.to(pusherNode, 0.2,
      { flexBasis: '100%', ease: Power3.easeInOut }), 0.5);

    return timeline;
  }

  handleBlankGroupClick = () => {
    this.props.history.push(`/groups/${NEW_GROUP_ID}`);
  }

  buildClosedGroupCard = (group: FriendGroup) => {
    const {
      classes,
      onCardClick,
    } = this.props;
    return (
      <GroupCard
        className={classes.closedCard}
        group={group}
        key={group.id}
        onClick={onCardClick}
      />
    );
  }

  buildBlankGroup = () => {
    const {
      classes,
    } = this.props;

    return (
      <Card
        className={classes.closedCard}
        key="temporary"
        onClick={this.handleBlankGroupClick}
      >
        <GroupDetails name={t('your group')} temporary />
      </Card>
    );
  }

  buildOpenGroupCard = (group: FriendGroup) => {
    const {
      classes,
    } = this.props;
    const {
      animating,
      isOpen,
    } = this.state;

    return (
      <div className={classes.openGroup} key={group.id}>
        <div className={classNames(classes.openOverlay, { visible: animating })}>
          <div className={classes.titleContainer}>
            <div className={classNames(classes.pusher, 'pusher')} />
            <div className={classes.title}>{group.name}</div>
          </div>
        </div>
        {(!animating || isOpen) && <GroupContentsExisting group={group} />}
      </div>
    );
  }

  render() {
    const {
      groups,
      currentGroupId,
      classes,
     } = this.props;
    const { isOpen, animating } = this.state;

    const currentIndex = groups.findIndex(g => g.id === currentGroupId);
    const noGroups = groups.length === 0;

    return (
      <div className={classes.carouselContainers}>
        {noGroups && this.buildBlankGroup()}
        {!noGroups && (isOpen || animating) &&
          <CarouselScrollable
            selectedIndex={currentIndex}
            ref={this.setOpenRef}
          >
            {groups.map(this.buildOpenGroupCard)}
          </CarouselScrollable>
        }
        {!noGroups && (animating || !isOpen) &&
          <CarouselScrollable
            selectedIndex={currentIndex}
            className={classes.carousel}
            ref={this.setClosedRef}
          >
            {groups.map(this.buildClosedGroupCard)}
          </CarouselScrollable>
        }
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    carousel: {
      width: '100%',
    },
    openOverlay: {
      padding: [8, 12, 4, 12],
      backgroundColor: theme.color.white,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0,
      borderRadius: 8,
      transition: 'opacity 0.2s',
      zIndex: 10,
      pointerEvents: 'none',
      '&.visible': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    carouselContainers: {
      flexGrow: 1,
      display: 'flex',
    },
    closedCard: {
      zIndex: 1,
      flexShrink: 0,
      marginLeft: 10,
      width: 155,
      height: 90,
      borderRadius: 8,
    },
    openGroup: {
      position: 'relative',
      boxSizing: 'content-box',
      scrollSnapType: 'proximity',
      marginLeft: 10,
      display: 'flex',
      width: 'calc(100% - 20px)',
      flexShrink: 0,
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'flex-end',

    },
    pusher: {
      flex: [1, 1, '0%'],
    },
    title: {
      textTransform: 'uppercase',
      color: theme.color.basis2,
      fontSize: 18,
      fontWeight: 600,
      flexGrow: 1,
      whiteSpace: 'nowrap',
      fontSmoothing: 'antialiased',
    },
  };
}

export default useSheet(applyStyles)(withRouter(GroupCardsMobile));
